import {
    ContextMenuInputPoints,
    ContextMenuInputValue,
} from '@thoughtspot/chart-common';
import {
    convertValueToBackend,
    getDateBucketBoundaries,
    isDateColumn,
} from '@thoughtspot/visualization-column-util';
import _ from 'lodash';
import { getUserSelectionPoints } from '/@services/answer/answer.util';
import {
    AnswerColumn,
    ChartViz,
    DrillDownColumn,
    DrillDownColumnInfo,
    TableViz,
    UserPointSelectionInput,
} from '/@services/generated/graphql-types';

function getClampedBoundaries(
    inputBoundaries: any,
    column: AnswerColumn,
    model: ChartViz | TableViz,
) {
    // If the input is not an interval or any information to clamp is unavailable, bail out.
    if (!model || !column || inputBoundaries.length !== 2) {
        return inputBoundaries;
    }

    return inputBoundaries;

    // TODO(@chetan): Revisit Clamping of Boundries with Filter Model
    // let filterModel: FilterModelV3 = answerModel.getCurrentAnswerSheet()
    //     .getFilterModelByColumn(column);
    // if (!filterModel) {
    //     return inputBoundaries;
    // }
    //
    // let filterColumn = filterModel.getColumn();
    // let isDateFilter = filterModel.getType() === FILTER_MODEL_TYPES.DATE;
    // if (!isDateFilter) {
    //     return inputBoundaries;
    // }
    //
    // // We use the Interval utility from luxon to clamp the provided date range to align
    // // with the date filter range in the answer.
    // // To use the utility, we have to prep the data appropriately.
    // // 1. Convert all epoch values to milliseconds.
    // // 2. Depending on the operator, change the interval so that the resulting range is inclusive.
    // // (Interval intersection expects ranges to be inclusive).
    // //
    // // For the unbounded comparison operators (GT, GE, LT, LE), we use the max javascript date
    // // boundaries as per the ECMAScript 5 convention (100 million days on either side of epoch):
    // // http://www.tutorialspoint.com/javascript/javascript_date_object.htm
    //
    // let filterConditions = filterModel.getConditions();
    // if (filterConditions.length !== 1) {
    //     return inputBoundaries;
    // }
    //
    // let filterCondition = filterConditions[0];
    //
    // let filterOperator = filterCondition.condition.op;
    // let filterValues = filterCondition.condition.values.map(
    //     (filterValue) => filterColumn.convertValueFromBackend(filterValue.intValue)
    // );
    //
    // let MAX_DATE_EPOCH = 8640000000000000,
    //     MIN_DATE_EPOCH = -8640000000000000;
    //
    // switch (filterOperator) {
    //     case sage.CompareTypeProto.E.EQ:
    //         // The excluded date value must not be in the range of selected points.
    //         if (filterCondition.condition.isNegate) {
    //             return inputBoundaries;
    //         }
    //         filterValues.push(filterValues[0]);
    //         break;
    //     case sage.CompareTypeProto.E.GT:
    //         // For Greater Than, advance the value by 1 millisecond and treat this as Greater Than
    //         // Equal.
    //         filterValues[0]++;
    //         filterValues.push(MAX_DATE_EPOCH);
    //         break;
    //     case sage.CompareTypeProto.E.GE:
    //         filterValues.push(MAX_DATE_EPOCH);
    //         break;
    //     case sage.CompareTypeProto.E.LT:
    //         // For Lesser Than, reduce the value by 1 millisecond and treat this as Lesser Than
    //         // Equal.
    //         filterValues[0]--;
    //         filterValues.push(filterValues[0]);
    //         filterValues[0] = MIN_DATE_EPOCH;
    //         break;
    //     case sage.CompareTypeProto.E.LE:
    //         filterValues.push(filterValues[0]);
    //         filterValues[0] = MIN_DATE_EPOCH;
    //         break;
    //     case sage.CompareTypeProto.E.BW:
    //         filterValues[0]++;
    //         filterValues[1]--;
    //         break;
    //     case sage.CompareTypeProto.E.BW_INC_MAX:
    //         filterValues[0]++;
    //         break;
    //     case sage.CompareTypeProto.E.BW_INC_MIN:
    //         filterValues[1]--;
    //         break;
    //     case sage.CompareTypeProto.E.BW_INC:
    //         // noop
    //         break;
    //     default:
    //         return inputBoundaries;
    // }
    //
    // let intersectionRange = getIntersectionRange(inputBoundaries[0],
    //     inputBoundaries[1], filterValues[0], filterValues[1]);
    //
    // if (!isIntervalValid(intersectionRange)) {
    //     return inputBoundaries;
    // }
    // return intervalToJSDateArray(intersectionRange);
}

// for time series values, the point would really represent 1 epoch value
// but in our filters for timeseries, we need to specify the start and end
// epoch values for the same. This function is to generated the same using
// the bucketing that we already specify in the column (eg. Daily or Monthly)
// and return the respective column value pair.
const getBucketedColumnValuePair = (
    columnValuePair: ContextMenuInputValue,
    model: ChartViz | TableViz,
    translateString: (val: string) => string,
) => {
    const column = columnValuePair.column;
    const parsedValue = convertValueToBackend(
        columnValuePair.column,
        parseInt(columnValuePair.value as string, 10),
    );
    if (!isDateColumn(column)) {
        return {
            column,
            value: parsedValue,
        };
    }

    let timeBucketBoundaries = getDateBucketBoundaries(
        columnValuePair.column,
        columnValuePair.value,
        null, // need to add custom calendar value map
        translateString,
    );
    if (!timeBucketBoundaries || timeBucketBoundaries.length <= 1) {
        return {
            column,
            value: parsedValue,
        };
    }

    timeBucketBoundaries = getClampedBoundaries(
        timeBucketBoundaries,
        column,
        model,
    );

    return {
        column,
        value: [
            convertValueToBackend(
                columnValuePair.column,
                parseInt(timeBucketBoundaries[0] as string, 10),
            ),
            convertValueToBackend(
                columnValuePair.column,
                parseInt(timeBucketBoundaries[1] as string, 10),
            ),
        ],
    };
};

export const getDrillTokens = (contextMenuPoints: ContextMenuInputPoints) => {
    // Regardless of what attributes are selected or not, always add all the
    // attributes in that row as filters, to make sure that we are drilling only the
    // data represented by that row.
    let { selectedPoints } = contextMenuPoints;
    if (
        !selectedPoints.find(pt =>
            _.isEqual(pt, contextMenuPoints.clickedPoint),
        )
    ) {
        selectedPoints = [contextMenuPoints.clickedPoint, ...selectedPoints];
    }
    const filteredAttributes = selectedPoints.reduce(
        (attributes, point) =>
            attributes.concat(
                point.selectedAttributes,
                point.deselectedAttributes,
            ),
        [] as ContextMenuInputValue[],
    );
    const selectedMeasures = ([] as ContextMenuInputValue[]).concat(
        ...selectedPoints.map(point => point.selectedMeasures),
    );
    const deselectedMeasures = ([] as ContextMenuInputValue[]).concat(
        ...selectedPoints.map(point => point.deselectedMeasures),
    );
    // If at least one measure is selected, drill that, else drill all the measures
    // present.
    const unfilteredAttributes = selectedMeasures.length
        ? selectedMeasures
        : deselectedMeasures;

    return {
        filteredAttributes,
        unfilteredAttributes,
    };
};

/**
 * GUIDs of the attribute columns the drill filters on, i.e. what the user
 * drilled *from*. A drill can start from more than one attribute (multi-select
 * on a chart, or every attribute in a clicked table row), so this is a list.
 */
export const getDrillFromColumnIds = (
    contextMenuPoints: ContextMenuInputPoints,
): string[] =>
    _.uniq(
        getDrillTokens(contextMenuPoints)
            .filteredAttributes.map(({ column }) => column?.id)
            .filter(Boolean),
    );

export type DrillDownAttributesType = {
    additionalFilters: UserPointSelectionInput[];
    nonFilteredColumns: string[];
    autoDrillDown: boolean;
    drillDownColumn?: DrillDownColumnInfo;
};

export const getDrillDownAttributes = (
    contextMenuPoints: ContextMenuInputPoints,
    autoDrillDown?: boolean,
    // Must specify column if autoDrillDown is false
    column?: Pick<DrillDownColumn, 'id'>,
): DrillDownAttributesType => {
    const { unfilteredAttributes } = getDrillTokens(contextMenuPoints);

    const additionalFilters: UserPointSelectionInput[] = getUserSelectionPoints(
        contextMenuPoints,
    );
    const nonFilteredColumns: string[] = unfilteredAttributes.map(
        (cvPair: ContextMenuInputValue) => cvPair.column.id,
    );

    if (!autoDrillDown && column) {
        const drillDownColumn: DrillDownColumnInfo = {
            logicalColumnId: column.id,
            // joinPath: drilldownColumn
        };
        return {
            additionalFilters,
            drillDownColumn,
            nonFilteredColumns,
            autoDrillDown: false,
        };
    }

    return {
        additionalFilters,
        nonFilteredColumns,
        autoDrillDown: true,
    };
};
